import InfoIcon from '@mui/icons-material/Info'
import {
  BaseTextFieldProps,
  Box,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone'
import 'react-international-phone/style.css'

export interface MUIPhoneProps extends BaseTextFieldProps {
  value: string
  onChange: (phone: string) => void
  onPhoneChange?: (phone: string) => void
  label: string
}

export const MuiPhone: React.FC<MUIPhoneProps> = ({
  label,
  value,
  onChange,
  onPhoneChange,
  ...restProps
}) => {
  // Use refs to always hold the latest callbacks.
  // usePhoneInput's internal state history captures onChange in a closure
  // that can go stale across re-renders, so we indirect through refs.
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange
  const onPhoneChangeRef = React.useRef(onPhoneChange)
  onPhoneChangeRef.current = onPhoneChange

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: 'gb',
      value: value || '',
      forceDialCode: true,
      countries: defaultCountries,
      onChange: (data) => {
        onChangeRef.current(data.phone)
        onPhoneChangeRef.current?.(data.phone)
      },
    })

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text')
    if (!pasted) return
    e.preventDefault()

    const dialCode = country?.dialCode ?? ''
    const trimmed = pasted.trim()
    const isInternational =
      trimmed.startsWith('+') || trimmed.startsWith('00')

    let digits = trimmed.replace(/\D/g, '')
    if (digits.startsWith('00')) digits = digits.slice(2)
    if (isInternational && dialCode && digits.startsWith(dialCode)) {
      digits = digits.slice(dialCode.length)
    }
    digits = digits.replace(/^0+/, '')

    const newValue = `+${dialCode}${digits}`

    handlePhoneValueChange({
      preventDefault: () => {},
      nativeEvent: { inputType: 'insertFromPaste' },
      target: { value: newValue, selectionStart: newValue.length },
    } as unknown as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <TextField
      variant="outlined"
      label={label}
      color="primary"
      value={inputValue || ''}
      onChange={handlePhoneValueChange}
      onPaste={handlePaste}
      type="tel"
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            style={{ marginRight: '2px', marginLeft: '-8px' }}
          >
            <Select
              MenuProps={{
                style: {
                  height: '300px',
                  width: '360px',
                  top: '10px',
                  left: '-34px',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              sx={{
                border: '0px',
                width: 'max-content',
                // Remove default outline (display only on focus)
                fieldset: {
                  display: 'none',
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: 'block',
                  },
                },
                // Update default spacing
                '.MuiSelect-select': {
                  padding: '8px',
                  paddingRight: '24px !important',
                },
                svg: {
                  right: 0,
                },
              }}
              value={country?.iso2 || ''}
              onChange={(e) =>
                setCountry((e.target.value as CountryIso2) || '')
              }
              renderValue={(value) => {
                return (
                  <Box sx={{ marginTop: '3px' }}>
                    <FlagImage iso2={value} style={{ display: 'flex' }} />
                  </Box>
                )
              }}
            >
              {defaultCountries.map((c) => {
                const country = parseCountry(c)
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage
                      iso2={country.iso2}
                      style={{ display: 'flex' }}
                    />

                    <Typography marginX="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                )
              })}
            </Select>
          </InputAdornment>
        ),
        endAdornment: !restProps.disabled && (
          <InputAdornment position="end">
            <Tooltip title="Please enter your phone number in E.164 format. Start with the country code (e.g., +1 for the USA, +44 for the UK) and drop the leading 0 from the national number. Example: +1234567890.">
              <InfoIcon />
            </Tooltip>
          </InputAdornment>
        ),
      }}
      {...restProps}
    />
  )
}
